import { useEffect, useState, useCallback } from 'react';

const Toast = ({
  message,
  type = 'info',
  title,
  confirmText,
  dismissText,
  cancelText,
  showCancel,
  onConfirm,
  onCancel,
  onClose,
  duration
}) => {
  const isWarning = type === 'warning';

  // Calculate dynamic reading duration based on text length: min 4.5s, max 10s
  const totalDuration = duration || Math.max(4500, Math.min(10000, (message?.length || 20) * 70));
  const [remainingTime, setRemainingTime] = useState(totalDuration);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-dismiss logic for standard toasts (warnings do NOT auto-dismiss)
  useEffect(() => {
    if (isWarning) return;

    const interval = 50;
    const timer = setInterval(() => {
      if (!isPaused) {
        setRemainingTime((prev) => {
          if (prev <= interval) {
            clearInterval(timer);
            onClose();
            return 0;
          }
          return prev - interval;
        });
      }
    }, interval);

    return () => clearInterval(timer);
  }, [isWarning, isPaused, onClose]);

  // Handle Keyboard (Escape / Enter) for Warning Pop-up Modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      if ((showCancel || onCancel) && onCancel) {
        onCancel();
      }
      onClose();
    } else if (e.key === 'Enter') {
      if (onConfirm) onConfirm();
      onClose();
    }
  }, [showCancel, onCancel, onConfirm, onClose]);

  useEffect(() => {
    if (isWarning) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isWarning, handleKeyDown]);

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    onClose();
  };

  const handleDismiss = () => {
    if ((showCancel || onCancel) && onCancel) onCancel();
    onClose();
  };

  // Strip leading warning emoji if present for clean title/message presentation
  const cleanMessage = (message || '').replace(/^⚠️\s*/, '');

  // ==========================================
  // 1. WARNING: Modern Pop-up Modal with OK / Dismiss
  // ==========================================
  if (isWarning) {
    return (
      <div
        className="no-print fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-fadein"
        onClick={handleDismiss}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="bg-white dark:bg-gray-900 border border-amber-200/90 dark:border-amber-800/80 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden relative transform transition-all animate-scalein"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top Decorative Ambient Accent */}
          <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500" />
          <div className="absolute -top-12 -right-12 w-32 h-32 bg-amber-500/10 dark:bg-amber-400/10 rounded-full blur-2xl pointer-events-none" />

          <div className="p-6">
            <div className="flex items-start gap-4">
              {/* Amber Glowing Icon Badge */}
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-950/80 dark:to-orange-950/60 border border-amber-300/80 dark:border-amber-700/60 flex items-center justify-center text-amber-600 dark:text-amber-400 flex-shrink-0 shadow-inner">
                <i className="fa-solid fa-triangle-exclamation text-2xl"></i>
              </div>

              {/* Content Details */}
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white tracking-tight">
                    {title || 'Warning'}
                  </h3>
                  <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/70 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800">
                    Attention
                  </span>
                </div>
                <p className="mt-2.5 text-sm text-gray-600 dark:text-gray-300 leading-relaxed font-normal">
                  {cleanMessage}
                </p>
              </div>
            </div>

            {/* Action Button(s) */}
            <div className="mt-6 flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              {(showCancel || onCancel) && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-gray-400 cursor-pointer"
                >
                  {cancelText || 'Cancel'}
                </button>
              )}
              <button
                type="button"
                autoFocus
                onClick={handleConfirm}
                className="w-full sm:w-auto px-6 py-2.5 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-md shadow-amber-500/20 rounded-xl transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-amber-500 flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <i className="fa-solid fa-check text-xs"></i>
                <span>{confirmText || 'OK'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // 2. STANDARD TOAST: Enhanced duration, pause-on-hover & dismiss button
  // ==========================================
  const styleConfig = {
    error: {
      bg: 'bg-gradient-to-r from-red-600 to-rose-700 dark:from-red-700 dark:to-rose-850',
      border: 'border-red-400/40 dark:border-red-600/40',
      icon: 'fa-circle-exclamation',
      iconBg: 'bg-red-500/30 text-white',
      progressBg: 'bg-red-300/60'
    },
    success: {
      bg: 'bg-gradient-to-r from-emerald-600 to-teal-700 dark:from-emerald-700 dark:to-teal-850',
      border: 'border-emerald-400/40 dark:border-emerald-600/40',
      icon: 'fa-circle-check',
      iconBg: 'bg-emerald-500/30 text-white',
      progressBg: 'bg-emerald-300/60'
    },
    info: {
      bg: 'bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-700 dark:to-indigo-850',
      border: 'border-blue-400/40 dark:border-blue-600/40',
      icon: 'fa-circle-info',
      iconBg: 'bg-blue-500/30 text-white',
      progressBg: 'bg-blue-300/60'
    }
  };

  const currentStyle = styleConfig[type] || styleConfig.info;
  const progressPercent = Math.max(0, Math.min(100, (remainingTime / totalDuration) * 100));

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className={`no-print fixed bottom-6 right-4 sm:right-8 z-50 max-w-[92vw] sm:max-w-md ${currentStyle.bg} ${currentStyle.border} border rounded-2xl shadow-2xl text-white overflow-hidden backdrop-blur-md transition-all duration-200 animate-slide-up flex flex-col`}
    >
      <div className="px-4 py-3.5 flex items-start gap-3">
        <div className={`mt-0.5 w-7 h-7 rounded-xl flex items-center justify-center ${currentStyle.iconBg} flex-shrink-0 text-sm`}>
          <i className={`fa-solid ${currentStyle.icon}`}></i>
        </div>

        <div className="flex-1 min-w-0 pr-1">
          <p className="text-xs sm:text-sm font-medium leading-relaxed text-gray-100">
            {message}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="text-gray-300 hover:text-white p-1 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0 cursor-pointer"
          title="Dismiss"
        >
          <i className="fa-solid fa-xmark text-sm"></i>
        </button>
      </div>

      {/* Visual progress bar line */}
      <div className="h-1 w-full bg-black/20">
        <div
          className={`h-full ${currentStyle.progressBg} transition-all duration-100 ease-linear`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
};

export default Toast;
